import { ArrowLeft, Camera, ChevronDown, List, Palette, X, HelpCircle, Check, ChevronRight } from "lucide-react";
import { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { useSnapshot } from "valtio";
import { store } from "../store";
import { shoes } from "../config";
import { Logo } from "./Logo";
import { getPartName } from "../helpers/getPartName";
import { useDownloadCanvas } from "../hooks/useDownloadCanvas";
import { TheCanvas } from "./TheCanvas";
import CollapsibleFooter from "./CollapsibleFooter";

const CUSTOMIZATION_STEPS = [
  { id: "style", title: "Wählen Sie Ihren Stil", icon: List },
  { id: "colors", title: "Farben anpassen", icon: Palette },
];

const SectionTitle = ({ icon: Icon, children, helpText }) => (
  <div className="mb-3">
    <h2 className="flex items-center text-lg font-semibold">
      <Icon className="w-5 h-5 mr-2" />
      {children}
      {helpText && (
        <button className="ml-2 text-white/60 hover:text-white" title={helpText}>
          <HelpCircle className="w-4 h-4" />
        </button>
      )}
    </h2>
    {helpText && <p className="mt-1 text-sm text-white/70">{helpText}</p>}
  </div>
);

const ActionButton = ({ onClick, children, primary = false, className = "", disabled = false }) => {
  return (
    <button
      disabled={disabled}
      className={`
        flex items-center justify-center py-2 px-4 rounded-md 
        transition-all duration-200 
        ${
          primary
            ? "bg-gray-800 text-white hover:bg-gray-700 disabled:bg-gray-600"
            : "bg-white/10 text-white hover:bg-white/20 disabled:bg-white/5"
        } 
        ${disabled ? "cursor-not-allowed opacity-50" : ""}
        ${className}
      `}
      onClick={onClick}>
      {children}
    </button>
  );
};

export function Configurator() {
  const snap = useSnapshot(store);
  const [isOpen, setIsOpen] = useState(false);
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const downloadCanvas = useDownloadCanvas();

  const handleStepComplete = (stepId) => {
    setCompletedSteps((prev) => new Set([...prev, stepId]));
    if (currentStep < CUSTOMIZATION_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const isStepComplete = (stepId) => completedSteps.has(stepId);

  const handleBack = () => {
    setCurrentStep(Math.max(currentStep - 1, 0));
  };

  return (
    <>
      <div className="fixed top-0 left-0 h-screen w-full lg:grid grid-cols-1 lg:grid-cols-[1fr,384px]">
        <div className="relative h-full">
          <div
            className={`
              absolute z-20 top-0 left-0 right-0 p-3 lg:px-20 
              bg-black/80 lg:bg-transparent text-white lg:text-black 
              flex justify-between lg:justify-start gap-5 items-center
              ${isOpen ? "hidden md:flex" : ""}
            `}>
            <div className="w-12 h-12 lg:w-24 lg:h-24 invert lg:invert-0">
              <Logo className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col justify-center lg:text-5xl" onClick={() => setIsOpen(true)}>
              <span>Schuh-Konfigurator</span>
              <span className="text-sm text-white/70 lg:text-black/70 lg:text-lg">
                Schritt {currentStep + 1} von {CUSTOMIZATION_STEPS.length}: {CUSTOMIZATION_STEPS[currentStep].title}
              </span>
            </div>
            <button className="lg:hidden p-2 hover:bg-white/10 rounded-full" onClick={() => setIsOpen(!isOpen)}>
              <ChevronDown className="w-6 h-6" />
            </button>
          </div>

          <div className="h-full">
            <TheCanvas />
          </div>
          <CollapsibleFooter />
        </div>

        <div
          className={`bruh
            absolute h-full inset-x-0 bottom-0 bg-black/70 text-white backdrop-blur-xs
            transition-transform duration-300 ease-in-out
            ${isOpen ? "translate-y-0" : "-translate-y-full"}
            lg:translate-y-0 lg:relative lg:h-full lg:backdrop-blur-xl
          `}>
          <div className="relative h-screen overflow-auto">
            <div className="flex justify-between items-center p-4 border-b border-white/20 sticky top-0 bg-black/70 backdrop-blur-xl z-10">
              <div className="lg:hidden flex items-center space-x-2">
                {currentStep !== 0 && (
                  <ActionButton onClick={handleBack} className="p-2">
                    <ArrowLeft className="w-5 h-5" />
                  </ActionButton>
                )}
              </div>
              <div className="hidden lg:block text-lg font-semibold">{CUSTOMIZATION_STEPS[currentStep].title}</div>
              <button className="lg:hidden p-2 hover:bg-white/10 rounded-full" onClick={() => setIsOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-4 pt-4">
              <div className="flex items-center space-x-2 mb-6">
                {CUSTOMIZATION_STEPS.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <button
                      onClick={() => setCurrentStep(index)}
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center
                        transition-all duration-200
                        ${
                          currentStep === index
                            ? "bg-white text-black"
                            : isStepComplete(step.id)
                            ? "bg-green-500 text-white"
                            : "bg-white/20 text-white"
                        }
                      `}>
                      {isStepComplete(step.id) ? <Check className="w-4 h-4" /> : index + 1}
                    </button>
                    {index < CUSTOMIZATION_STEPS.length - 1 && (
                      <div
                        className={`
                          h-0.5 w-8 mx-2
                          ${isStepComplete(step.id) ? "bg-green-500" : "bg-white/20"}
                        `}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 space-y-6">
              {currentStep === 0 && (
                <Section title="Wählen Sie Ihren Stil" icon={List} helpText="Wählen Sie den Grundstil für Ihren individuellen Schuh">
                  <div className="grid grid-cols-2 gap-3">
                    {Object.keys(shoes).map((shoe) => (
                      <ShoeTypeButton
                        key={shoe}
                        name={shoe}
                        selected={snap.selectedShoeType === shoe}
                        onClick={() => {
                          store.selectedShoeType = shoe;
                          handleStepComplete("style");
                        }}
                      />
                    ))}
                  </div>
                </Section>
              )}

              {currentStep === 1 && (
                <Section title="Farben anpassen" icon={Palette} helpText="Klicken Sie auf einen Bereich, um die Farbe zu ändern">
                  <div className="space-y-3">
                    {Object.keys(shoes[snap.selectedShoeType].components).map((itemName) => (
                      <ColorItem
                        key={itemName}
                        name={itemName}
                        isActive={activeColorPicker === itemName}
                        onSelect={() => setActiveColorPicker(itemName === activeColorPicker ? null : itemName)}
                      />
                    ))}
                  </div>
                  <div className="mt-6">
                    <ActionButton onClick={() => handleStepComplete("colors")} primary className="w-full">
                      Farbauswahl abschließen <ChevronRight className="w-4 h-4 ml-2" />
                    </ActionButton>
                  </div>
                </Section>
              )}
            </div>

            <div className="p-4 border-t border-white/20 space-y-3">
              <div className="flex gap-3">
                {currentStep !== 0 && (
                  <ActionButton onClick={handleBack} className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
                  </ActionButton>
                )}
                <ActionButton onClick={downloadCanvas} primary className="flex-1" disabled={!isStepComplete("colors")}>
                  <Camera className="w-4 h-4 mr-2" /> Design speichern
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeColorPicker && (
        <div className="fixed inset-0 bg-black/50 z-30 flex items-center justify-center p-4" onClick={() => setActiveColorPicker(null)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">{getPartName(activeColorPicker)}</h3>
                <p className="text-sm text-gray-500">Wählen Sie Ihre perfekte Farbe</p>
              </div>
              <button className="p-2 hover:bg-gray-100 rounded-full" onClick={() => setActiveColorPicker(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <HexColorPicker
              color={store.items[activeColorPicker]}
              onChange={(color) => (store.items[activeColorPicker] = color)}
              className="w-full"
            />
            <button
              className="mt-4 w-full bg-gray-800 text-white py-2 rounded-md hover:bg-gray-700 transition-colors"
              onClick={() => setActiveColorPicker(null)}>
              Farbe anwenden
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const Section = ({ title, icon, children, helpText }) => (
  <div>
    <SectionTitle icon={icon} helpText={helpText}>
      {title}
    </SectionTitle>
    {children}
  </div>
);

const ShoeTypeButton = ({ name, selected, onClick }) => {
  const { thumbnailUrl } = shoes[name];

  return (
    <button
      className={`
        relative aspect-square rounded-lg overflow-hidden
        transition-all duration-200 group
        ${selected ? "ring-2 ring-white" : "hover:ring-2 hover:ring-white/50"}
      `}
      onClick={onClick}>
      <img src={thumbnailUrl} alt={getPartName(name)} className="w-full h-full object-cover" />
      <div
        className={`
          absolute inset-0 flex items-center justify-center
          bg-black/40 transition-opacity duration-200
          ${selected ? "opacity-0" : "opacity-0 group-hover:opacity-100"}
        `}>
        <span className="text-white font-medium">{getPartName(name)}</span>
      </div>
      {selected && (
        <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </button>
  );
};

const ColorItem = ({ name, isActive, onSelect }) => {
  const snap = useSnapshot(store);
  const color = store.items[name];

  return (
    <button
      className={`
        w-full flex items-center gap-3 p-3 rounded-lg
        transition-all duration-200
        ${isActive ? "bg-white/20" : "hover:bg-white/10"}
      `}
      onClick={onSelect}>
      <div className="w-8 h-8 rounded-full shadow-inner ring-2 ring-white/20" style={{ backgroundColor: color }} />
      <div className="flex-1 text-left">
        <div>{getPartName(name)}</div>
        <div className="text-sm text-white/60">Klicken Sie zum Ändern der Farbe</div>
      </div>
    </button>
  );
};

export default Configurator;
